import React, { useState, useEffect } from "react";
import { themeEmojis } from "../utils/themeEmojis";
import { useZodiacStore } from "../store/useZodiacStore";
import { getCurrentZodiacSign, zodiacData } from "../utils/zodiacUtils";

function DailyReading({ card }) {
  const [copied, setCopied] = useState(false);
  const selectedZodiac = useZodiacStore((state) => state.selectedZodiac);
  const currentDaySign = getCurrentZodiacSign();

  if (!card) return null;

  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const year = today.getFullYear();
  const formattedDate = `${day} / ${month} / ${year}`;

  // Use present narrative if available, otherwise fallback to general meaning
  const presentMeaning =
    card.narrative?.present ||
    card.meaning ||
    "No specific guidance available for today.";

  // Construct image path (assuming images are in public/images)
  const imagePath = card.image; // Use the path directly from the card data

  const handleCopyDaily = () => {
    const themes = card.theme || [];
    const cardEmoji = themeEmojis[themes[0]] || "✨"; // Default to sparkle if no theme/emoji
    const insightEmoji1 = themeEmojis[themes[1]] || "";
    const insightEmoji2 = themeEmojis[themes[2]] || "";
    const insightEmojis = [insightEmoji1, insightEmoji2]
      .filter(Boolean)
      .join(" "); // Join with space, remove empty

    // Determine which sign context to use for copy
    const signContext = selectedZodiac
      ? `For your selected sign: ${selectedZodiac}`
      : `Today's Sun Sign: ${currentDaySign}`;

    const textToCopy = `DAILY CARD: ${card.name} ${cardEmoji}
INSIGHT: ${presentMeaning}${insightEmojis ? " " + insightEmojis : ""}
ASTRO NOTE: ${signContext}

--------------------

FROM: READMEtarot visit https://alwaysnoticing.com
    `.trim();

    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setCopied(true);
        // Reset the copied state after 2 seconds
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy daily card: ", err);
        alert("Failed to copy daily card.");
      });
  };

  // Determine the relevant sign and its details
  const relevantSignName = selectedZodiac || currentDaySign;
  const signDetails = relevantSignName ? zodiacData[relevantSignName] : null;

  return (
    // Main container: vertical stack, full width
    <div className="w-full flex flex-col items-start gap-4 text-left">
      {/* --- Text Block --- */}
      <div className="w-full">
        <p className="text-sm text-custom-red mb-1">{formattedDate}</p>
        <p className="text-lg text-custom-red mb-3 font-medium">
          Your daily reading...
        </p>
        {/* Combined Card Name and Meaning */}
        <p className="text-base text-gray-700 mb-4">
          <strong className="text-gray-800">{card.name}:</strong>{" "}
          {presentMeaning}
        </p>
      </div>

      {/* --- Card Image --- */}
      {/* Container to manage image size/position, matching button width */}
      <div className="flex-shrink-0 w-full max-w-xs mb-4">
        {" "}
        {/* Constrain width, add bottom margin */}
        <img
          src={imagePath}
          alt={card.name}
          className="w-full h-auto object-contain border border-gray-200"
        />
      </div>

      {/* --- Zodiac Info & Copy Button Block --- */}
      <div className="w-full flex flex-col items-start">
        {/* Display Sign Info */}
        {relevantSignName && signDetails && (
          <div className="mb-4 text-base text-gray-600 flex flex-col items-start">
            {" "}
            {/* Stack icon/text vertically */}
            {/* Zodiac Image */}
            <img
              src={`/images/zodiac/${relevantSignName.toLowerCase()}.jpg`}
              alt={`${relevantSignName} sign`}
              className="w-full max-w-xs h-auto mb-2 object-contain border border-gray-200"
            />
            {/* Sign Details Text Block */}
            <div>
              <p>
                <span className="font-bold text-gray-800">
                  {selectedZodiac ? "Your Selected Sign:" : "Today's Sun Sign:"}
                </span>{" "}
                <span className="font-medium text-custom-red">
                  {signDetails.glyph} {relevantSignName}
                </span>
              </p>
              <p className="text-sm mt-1">
                {signDetails.element} {signDetails.modality} // Ruled by{" "}
                {signDetails.rulingPlanet}
                {signDetails.traditionalRuler &&
                  ` (Trad: ${signDetails.traditionalRuler})`}
              </p>
              <p className="text-sm mt-1 italic">
                Keywords: {signDetails.keywords.slice(0, 3).join(", ")}...
              </p>
            </div>
          </div>
        )}

        {/* Copy Button */}
        <button
          onClick={handleCopyDaily}
          className={`px-3 py-1 border text-xs transition-colors duration-150 flex items-center w-40 justify-center 
            ${
              copied
                ? "bg-green-100 border-green-300 text-green-700"
                : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
            }
          `}
          disabled={copied}
        >
          {copied ? (
            <>
              <span role="img" aria-label="check" className="mr-1">
                ✅
              </span>{" "}
              Copied!
            </>
          ) : (
            <>
              <span role="img" aria-label="clipboard" className="mr-1">
                📋
              </span>{" "}
              Copy Daily Reading
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default DailyReading;
